import { useState, useEffect, useRef } from "react";
import { Route, Switch, Link, useLocation } from "react-router-dom";
import SwipeRush from "./pages/SwipeRush";
import SwipeRushPrivacy from "./pages/SwipeRushPrivacy";
import SwipeRushTerms from "./pages/SwipeRushTerms";
import BasicWebsite from "./pages/BasicWebsite";
import PaymentSuccess from "./pages/PaymentSuccess";

// ─── Contact Form Formspree Endpoint ─────────────────────────────────────────
// Sign up at https://formspree.io → create a new form → paste the endpoint here
var CONTACT_FORMSPREE_ENDPOINT = "https://formspree.io/f/mykbedgr";
// ─────────────────────────────────────────────────────────────────────────────

// ─── Scroll Reveal Hook ────────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(function () {
    var elements = document.querySelectorAll(".reveal");
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });

    return function () {
      observer.disconnect();
    };
  }, []);
}

// ─── Counter Hook ─────────────────────────────────────────────────────────────
function useCounter(targetRef, end, duration) {
  var countRef = useRef(null);

  useEffect(function () {
    var el = targetRef.current;
    if (!el) return;

    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          var startTime = null;

          function step(timestamp) {
            if (!startTime) startTime = timestamp;
            var progress = Math.min((timestamp - startTime) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.floor(eased * end);
            if (progress < 1) {
              requestAnimationFrame(step);
            } else {
              el.textContent = end;
            }
          }

          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    countRef.current = observer;

    return function () {
      observer.disconnect();
    };
  }, [targetRef, end, duration]);
}

// ─── StatCounter Component ─────────────────────────────────────────────────────
function StatCounter(props) {
  var ref = useRef(null);
  useCounter(ref, props.end, props.duration || 1800);

  return (
    <div className={"stat-item reveal reveal-delay-" + (props.delay || "1")}>
      <div className={"stat-number"}>
        <span ref={ref}>0</span>
        <span>{props.suffix}</span>
      </div>
      <div className={"stat-label"}>{props.label}</div>
    </div>
  );
}

// ─── HomePage Component ────────────────────────────────────────────────────────
function HomePage(props) {
  var clientLogos = props.clientLogos;
  var customerReview = props.customerReview;
  var showCustomerReview = props.showCustomerReview;

  useScrollReveal();

  // ── Contact form state ───────────────────────────────────────────────────
  var [contactForm, setContactForm]     = useState({ name: "", email: "", phone: "", purpose: "" });
  var [contactErrors, setContactErrors] = useState({});
  var [contactStatus, setContactStatus] = useState("idle"); // idle | sending | success | error

  function handleContactChange(e) {
    var field = e.target.name;
    var value = e.target.value;
    setContactForm(function (prev) { return Object.assign({}, prev, { [field]: value }); });
    if (contactErrors[field]) {
      setContactErrors(function (prev) { return Object.assign({}, prev, { [field]: "" }); });
    }
  }

  function validateContact() {
    var errs = {};
    if (!contactForm.name.trim())  errs.name  = "Name is required.";
    if (!contactForm.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) errs.email = "Enter a valid email.";
    if (!contactForm.phone.trim()) errs.phone = "Mobile number is required.";
    else if (!/^\+?[0-9\s\-()]{7,15}$/.test(contactForm.phone)) errs.phone = "Enter a valid mobile number.";
    if (!contactForm.purpose.trim()) errs.purpose = "Please tell us your purpose.";
    return errs;
  }

  function handleContactSubmit(e) {
    e.preventDefault();
    var errs = validateContact();
    if (Object.keys(errs).length > 0) { setContactErrors(errs); return; }
    setContactStatus("sending");
    fetch(CONTACT_FORMSPREE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({
        name:    contactForm.name.trim(),
        email:   contactForm.email.trim(),
        phone:   contactForm.phone.trim(),
        purpose: contactForm.purpose.trim(),
      }),
    })
      .then(function (res) {
        if (res.ok) {
          setContactStatus("success");
          setContactForm({ name: "", email: "", phone: "", purpose: "" });
        } else {
          setContactStatus("error");
        }
      })
      .catch(function () { setContactStatus("error"); });
  }

  // ── Parallax: move hero layers at different rates on scroll ──────────────
  var heroRef = useRef(null);
  useEffect(function () {
    function onScroll() {
      if (!heroRef.current) return;
      var y = window.scrollY;
      heroRef.current.style.setProperty('--px-y', y + 'px');
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return function () { window.removeEventListener('scroll', onScroll); };
  }, []);

  // ── Dock zoom: track hovered logo index for neighbour scaling ────────────
  var [dockHover, setDockHover] = useState(null);
  var total = clientLogos.length;

  function dockScale(rawIndex) {
    if (dockHover === null) return 1;
    var idx = rawIndex % total;
    var hovIdx = dockHover % total;
    var dist = Math.abs(idx - hovIdx);
    dist = Math.min(dist, total - dist); // wrap-around
    if (dist === 0) return 1.9;
    if (dist === 1) return 1.4;
    if (dist === 2) return 1.15;
    return 1;
  }

  var activeReview = customerReview.find(function (r) { return r.active; }) || customerReview[0];

  var services = [
    {
      icon: "🎨",
      title: "Web Design & Development",
      desc: "Full-stack web solutions for businesses of all sizes — from custom web apps and e-commerce to CMS platforms and social networking sites. Built with attention to detail to satisfy your specific needs.",
    },
    {
      icon: "📱",
      title: "Mobile Apps (Android & iOS)",
      desc: "World-class, engaging iOS and Android apps your customers love and use. Native, cross-platform, or hybrid — our expert team knows these platforms inside out.",
    },
    {
      icon: "🤖",
      title: "AI & Machine Learning",
      desc: "Custom ML models and intelligent automation pipelines that give your product a cognitive edge — from predictive features to personalised user experiences.",
    },
    {
      icon: "✨",
      title: "Generative AI Integration",
      desc: "LLM-powered features, RAG pipelines, and GenAI product development — helping clients stay ahead in an increasingly digital and data-driven world.",
    },
    {
      icon: "🧠",
      title: "Deep Learning & Computer Vision",
      desc: "Neural networks for image recognition, NLP, predictive analytics, and real-time inference — building smarter, more adaptive applications.",
    },
    {
      icon: "📣",
      title: "Digital Marketing",
      desc: "Full-service digital marketing — SEO, search engine marketing, social media marketing, and email marketing — driving meaningful traffic that helps your business grow.",
    },
    {
      icon: "🛍️",
      title: "E-Commerce Solutions",
      desc: "Scalable online stores with secure payment gateways, inventory management, and conversion-optimised UX built to grow with your business.",
    },
    {
      icon: "🗂️",
      title: "CMS Solutions",
      desc: "Intuitive content management systems that empower your team to publish and iterate without needing developers every time.",
    },
    {
      icon: "☁️",
      title: "Managed Web Hosting",
      desc: "Reliable, high-availability hosting with proactive monitoring, security patching, domain registration, and dedicated technical support.",
    },
  ];

  return (
    <>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section id={"home"} ref={heroRef}>
        <div className={"hero-inner hero-parallax-content"}>
          <div className={"hero-badge"}>
            <span className={"badge-dot"}></span>
            <span className={"badge-text"}>EST. 2017 · Bangalore, India</span>
          </div>

          <h1 className={"hero-headline"}>
            WE BUILD
            <span className={"hero-headline-gradient"}>SMARTER</span>
            STUFF
          </h1>

          <p className={"hero-tagline"}>
            We Build Smarter, Highly Usable Products — Transforming ideas into
            powerful digital experiences since 2017.
          </p>

          <div className={"hero-ctas"}>
            <a href={"#work"} className={"btn-primary"}>
              Our Work
            </a>
            <a href={"mailto:contact@nuevoalture.com"} className={"btn-ghost"}>
              Get In Touch
            </a>
          </div>

          <div className={"hero-scroll-indicator"}>
            <span className={"scroll-line"}></span>
            <span>Scroll to explore</span>
          </div>
        </div>

        <div className={"hero-est-badge"}>
          <span className={"est-year"}>09</span>
          <span className={"est-label"}>Years</span>
        </div>
      </section>

      {/* ─── About ────────────────────────────────────────────────────────── */}
      <section id={"about"}>
        <div className={"about-inner"}>
          {/* Stats row */}
          <div className={"stats-row"}>
            <StatCounter end={9} suffix={"+"} label={"Years of Experience"} delay={"1"} duration={1200} />
            <StatCounter end={12} suffix={"+"} label={"Clients Served"} delay={"2"} duration={1400} />
            <StatCounter end={2} suffix={""} label={"App Platforms"} delay={"3"} duration={900} />
            <StatCounter end={100} suffix={"%"} label={"Client Satisfaction"} delay={"4"} duration={1600} />
          </div>

          {/* Split: Who We Are + We Believe */}
          <div className={"about-split"}>
            <div className={"about-text-col reveal"}>
              <span className={"u-label"}>About us</span>
              <h2>WHO WE ARE</h2>
              <p>
                Nuevo Alture Tech Ventures is a forward-thinking technology company
                delivering IT-enabled Web and Mobile Application Development services
                since 2017. We are committed to transforming ideas into powerful digital
                experiences that are intuitive, scalable, and globally relevant.
              </p>
              <p>
                Our core focus lies in two areas — Web Solutions and Mobile App
                Development — empowering businesses and individuals with cutting-edge
                digital tools and seamless user experiences across platforms.
              </p>
              <p>
                As we evolve, we're also embracing the power of Artificial Intelligence
                to build smarter, more adaptive applications. From intelligent automation
                and predictive features to personalised user experiences, we integrate AI
                into our solutions to help clients stay ahead in an increasingly digital
                and data-driven world.
              </p>
              <p className={"about-tagline"}>
                At Nuevo Alture, we don't just build products — we build the future of
                digital interaction.
              </p>
            </div>

            <div className={"believe-card reveal reveal-delay-2"}>
              <p className={"believe-eyebrow"}>Our philosophy</p>
              <h3>WE BELIEVE</h3>
              <p className={"believe-subtitle"}>in Unleashing the Possibilities</p>
              <p>
                We help individuals / inventors / entrepreneurs / designers / developers /
                fresh-thinkers to achieve their dreams and goals by showing them how to
                increase their overall productivity, identity, and belief in their ability
                towards success. As you read our testimonials, you will find that many of
                our clients identify our rigorous accountability approach as well as our
                ability to increase their self-belief as the driving forces behind them
                remaining focused on pursuing and achieving their dreams and goals.
              </p>
              <p>
                Driven by creativity, guided by strategy, and fueled by technology — we're
                here to turn your vision into innovation.
              </p>
            </div>
          </div>

          {/* Vision / Mission cards */}
          <div className={"vision-mission-grid"}>
            <div className={"vm-card reveal reveal-delay-1"}>
              <img
                className={"vm-image"}
                src={require("./images/vision.jpg").default}
                alt={"Our Vision"}
              />
              <div className={"vm-overlay"}></div>
              <div className={"vm-content"}>
                <span className={"vm-tag"}>Vision</span>
                <h3>OUR VISION</h3>
                <p>
                  To be a leading force in web and mobile application development,
                  empowering individuals and businesses to transform their ideas into
                  impactful, real-world digital solutions.
                </p>
              </div>
            </div>

            <div className={"vm-card reveal reveal-delay-2"}>
              <img
                className={"vm-image"}
                src={require("./images/mission.jpg").default}
                alt={"Our Mission"}
              />
              <div className={"vm-overlay"}></div>
              <div className={"vm-content"}>
                <span className={"vm-tag"}>Mission</span>
                <h3>OUR MISSION</h3>
                <p>
                  To deliver 100% client satisfaction through adaptable, results-driven
                  project management — creating web and mobile solutions that meet global
                  standards and exceed expectations. We partner with ambitious clients and
                  go the extra mile to turn their goals into achievements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Services ─────────────────────────────────────────────────────── */}
      <section id={"services"}>
        <div className={"services-inner"}>
          <div className={"services-header"}>
            <div className={"services-title-block reveal"}>
              <span className={"u-label"}>What we do</span>
              <h2>WHAT WE DO</h2>
            </div>
            <p className={"services-desc reveal reveal-delay-2"}>
              Through the creative use of graphic design, digital technology, and online
              strategy, we solve problems and develop opportunities that help place our
              clients ahead of their competition.
            </p>
          </div>

          <div className={"services-grid"}>
            {services.map(function (service, index) {
              return (
                <div className={"service-card reveal reveal-delay-" + ((index % 3) + 1)} key={index}>
                  <p className={"service-card-number"}>
                    {"0" + (index + 1)}
                  </p>
                  <span className={"service-icon"}>{service.icon}</span>
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Portfolio / Work ─────────────────────────────────────────────── */}
      <section id={"work"}>
        <div className={"work-inner"}>
          <div className={"work-header reveal"}>
            <span className={"u-label"}>Selected Work</span>
            <h2>WHAT WE'VE BUILT</h2>
          </div>

          <div className={"work-grid"}>

            <div className={"work-card work-card--featured reveal reveal-delay-1"}>
              <div className={"work-card-tag"}>Mobile App</div>
              <h3>Flizone Stores</h3>
              <p>End-to-end Android &amp; iOS mobile application for Flizone Stores — delivered on time with full feature parity, smooth UX, and real-time inventory. "A great set of developers who understand every requirement."</p>
              <div className={"work-card-chips"}>
                <span>Android</span><span>iOS</span><span>Mobile App</span>
              </div>
            </div>

            <div className={"work-card reveal reveal-delay-2"}>
              <div className={"work-card-tag"}>Web Platform</div>
              <h3>Takreem Business</h3>
              <p>Digital platform for Takreem Business — a full web solution built to connect and empower their business network with a modern, scalable architecture.</p>
              <div className={"work-card-chips"}>
                <span>Web Dev</span><span>UI/UX</span>
              </div>
            </div>

            <div className={"work-card reveal reveal-delay-3"}>
              <div className={"work-card-tag"}>International Client · UAE</div>
              <h3>Eighty Six Media</h3>
              <p>Web design and development for Eighty Six Media, a UAE-based agency — demonstrating our capability to deliver globally competitive digital solutions.</p>
              <div className={"work-card-chips"}>
                <span>Web Design</span><span>International</span>
              </div>
            </div>

            <div className={"work-card reveal reveal-delay-1"}>
              <div className={"work-card-tag"}>Social Platform</div>
              <h3>NewBee Works</h3>
              <p>Community and social networking platform built for NewBee Works — engineered for engagement, user growth, and real-time interaction at scale.</p>
              <div className={"work-card-chips"}>
                <span>Social Network</span><span>Web Platform</span>
              </div>
            </div>

            <div className={"work-card reveal reveal-delay-2"}>
              <div className={"work-card-tag"}>Branding + Web</div>
              <h3>Ansal Infosys</h3>
              <p>Corporate website and branding for Ansal Infosys — modern UI, responsive layouts, and CMS integration. "They did an amazing job. I would recommend them for any development project."</p>
              <div className={"work-card-chips"}>
                <span>Web Design</span><span>CMS</span><span>Branding</span>
              </div>
            </div>

            <div className={"work-card work-card--ai reveal reveal-delay-3"}>
              <div className={"work-card-tag work-card-tag--ai"}>AI · In-house Product</div>
              <h3>SwipeRush &amp; GenAI</h3>
              <p>SwipeRush — our in-house mobile card game — plus ongoing AI/ML integration work building intelligent automation, LLM-powered features, and computer vision pipelines for clients.</p>
              <div className={"work-card-chips"}>
                <span>Mobile Game</span><span>LLM</span><span>Computer Vision</span><span>AI</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── Team ─────────────────────────────────────────────────────────── */}
      <section id={"team"}>
        <div className={"team-inner"}>
          <div className={"team-header reveal"}>
            <span className={"u-label"}>The People</span>
            <h2>OUR TEAM</h2>
            <p className={"team-subtitle reveal reveal-delay-1"}>
              Our strength lies in our people — a passionate, diverse team of developers,
              designers, strategists, and project managers united by a shared vision to
              build smarter, more impactful digital products.
            </p>
          </div>

          <div className={"team-grid"}>
            <div className={"team-card reveal reveal-delay-1"}>
              <div className={"team-card-avatar"}>
                <span>CS</span>
              </div>
              <div className={"team-card-info"}>
                <h3>Dr. Chandni Sinha</h3>
                <p className={"team-card-role"}>Founder &amp; CEO</p>
                <p className={"team-card-bio"}>
                  Visionary leader driving Nuevo Alture's mission to empower businesses
                  through technology. Passionate about innovation, strategy, and building
                  lasting client relationships.
                </p>
              </div>
            </div>

            <div className={"team-card reveal reveal-delay-2"}>
              <div className={"team-card-avatar"}>
                <span>SS</span>
              </div>
              <div className={"team-card-info"}>
                <h3>Shreyash Sinha</h3>
                <p className={"team-card-role"}>Co-Founder &amp; CTO</p>
                <p className={"team-card-bio"}>
                  Technology architect behind NA Tech Ventures products. Leads engineering
                  with deep expertise in web, mobile, and AI — ensuring every product
                  is scalable, performant, and built to last.
                </p>
              </div>
            </div>
          </div>

          <p className={"team-ethos reveal reveal-delay-3"}>
            We believe in continuous learning, staying ahead of the technology curve, and
            fostering a culture of trust, ownership, and excellence. Whether it's a startup
            idea or an enterprise challenge — our team is ready to build, scale, and support
            with confidence and care.
          </p>
        </div>
      </section>

      {/* ─── Testimonials ─────────────────────────────────────────────────── */}
      <section id={"customer-review"}>
        <div className={"testimonials-inner"}>
          <div className={"testimonials-header reveal"}>
            <span className={"u-label"}>Testimonials</span>
            <h2>WHAT OUR CUSTOMERS SAY</h2>
          </div>

          <div className={"testimonials-layout"}>
            <div className={"testimonials-list"}>
              {customerReview.map(function (review, index) {
                return (
                  <div
                    className={"review-card reveal reveal-delay-" + (index + 1) + (review.active ? " active" : "")}
                    key={index}
                    onClick={function () { showCustomerReview(index); }}
                  >
                    <span className={"review-quote-mark"}>"</span>
                    <p className={"review-text"}>
                      {review.active ? review.long : review.short}
                    </p>
                    <div className={"customer-info"}>
                      <div className={"customer-name"}>{review.name}</div>
                      <div className={"customer-designation"}>
                        {review.designation}, {review.organization}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={"testimonial-pic-panel reveal reveal-delay-2"}>
              {activeReview && (
                <>
                  <div className={"pic-frame"}>
                    <img
                      src={require("./images/ClientLogo/" + activeReview.image).default}
                      alt={activeReview.name + ", " + activeReview.organization}
                    />
                  </div>
                  <div className={"pic-caption"}>
                    <div className={"caption-name"}>{activeReview.name}</div>
                    <div className={"caption-org"}>
                      {activeReview.designation}, {activeReview.organization}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Clients Marquee ──────────────────────────────────────────────── */}
      <section id={"clients"}>
        <div className={"clients-header"}>
          <span className={"u-label reveal"}>Our clients</span>
          <h2 className={"reveal reveal-delay-1"}>TRUSTED BY GREAT COMPANIES</h2>
        </div>

        <div className={"marquee-wrapper"}>
          {/* Duplicate track for seamless infinite loop */}
          <div className={"marquee-track"}>
            {clientLogos.concat(clientLogos).map(function (item, index) {
              var scale = dockScale(index);
              var lift = (scale - 1) * 28;
              return (
                <div
                  className={"client-logo-item"}
                  key={index}
                  onMouseEnter={function () { setDockHover(index); }}
                  onMouseLeave={function () { setDockHover(null); }}
                >
                  <div
                    className={"logo-pill"}
                    style={{
                      transform: "scale(" + scale + ") translateY(-" + lift + "px)",
                      zIndex: scale > 1 ? 10 : 1,
                    }}
                  >
                    <img
                      src={require("./images/ClientLogo/" + item.clientLogo).default}
                      alt={item.clientName}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Get In Touch ─────────────────────────────────────────────────── */}
      <section id={"contact"}>
        <div className={"contact-inner"}>
          <div className={"contact-left reveal"}>
            <span className={"u-label"}>Get In Touch</span>
            <h2>LET'S TALK</h2>
            <p>Have a project in mind or just want to say hello? Fill in your details and we'll get back to you within 24 hours.</p>
            <div className={"contact-detail"}>
              <span className={"contact-detail-icon"}>✉</span>
              <a href={"mailto:contact@nuevoalture.com"}>contact@nuevoalture.com</a>
            </div>
            <div className={"contact-detail"}>
              <span className={"contact-detail-icon"}>📍</span>
              <span>Bangalore, India</span>
            </div>
          </div>

          <div className={"contact-right reveal reveal-delay-2"}>
            {contactStatus === "success" ? (
              <div className={"contact-success"}>
                <span className={"contact-success-icon"}>✓</span>
                <h3>Message Sent!</h3>
                <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                <button className={"btn-ghost"} onClick={function () { setContactStatus("idle"); }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form className={"contact-form"} onSubmit={handleContactSubmit} noValidate>
                <div className={"contact-form-row"}>
                  <div className={"contact-field"}>
                    <label htmlFor={"c-name"}>Full Name *</label>
                    <input
                      id={"c-name"} type={"text"} name={"name"}
                      value={contactForm.name} onChange={handleContactChange}
                      placeholder={"Your name"}
                      className={contactErrors.name ? "error" : ""}
                    />
                    {contactErrors.name && <span className={"c-field-error"}>{contactErrors.name}</span>}
                  </div>
                  <div className={"contact-field"}>
                    <label htmlFor={"c-email"}>Email Address *</label>
                    <input
                      id={"c-email"} type={"email"} name={"email"}
                      value={contactForm.email} onChange={handleContactChange}
                      placeholder={"you@example.com"}
                      className={contactErrors.email ? "error" : ""}
                    />
                    {contactErrors.email && <span className={"c-field-error"}>{contactErrors.email}</span>}
                  </div>
                </div>

                <div className={"contact-form-row"}>
                  <div className={"contact-field"}>
                    <label htmlFor={"c-phone"}>Mobile Number *</label>
                    <input
                      id={"c-phone"} type={"tel"} name={"phone"}
                      value={contactForm.phone} onChange={handleContactChange}
                      placeholder={"+91 98765 43210"}
                      className={contactErrors.phone ? "error" : ""}
                    />
                    {contactErrors.phone && <span className={"c-field-error"}>{contactErrors.phone}</span>}
                  </div>
                  <div className={"contact-field"}>
                    <label htmlFor={"c-purpose"}>Purpose *</label>
                    <select
                      id={"c-purpose"} name={"purpose"}
                      value={contactForm.purpose} onChange={handleContactChange}
                      className={contactErrors.purpose ? "error" : ""}
                    >
                      <option value={""}>Select a purpose…</option>
                      <option value={"Website Development"}>Website Development</option>
                      <option value={"Mobile App Development"}>Mobile App Development</option>
                      <option value={"AI / ML Project"}>AI / ML Project</option>
                      <option value={"Digital Marketing"}>Digital Marketing</option>
                      <option value={"E-Commerce Solution"}>E-Commerce Solution</option>
                      <option value={"General Inquiry"}>General Inquiry</option>
                      <option value={"Other"}>Other</option>
                    </select>
                    {contactErrors.purpose && <span className={"c-field-error"}>{contactErrors.purpose}</span>}
                  </div>
                </div>

                {contactStatus === "error" && (
                  <p className={"c-submit-error"}>Something went wrong. Please try again or email us directly.</p>
                )}

                <button type={"submit"} className={"contact-submit"} disabled={contactStatus === "sending"}>
                  {contactStatus === "sending" ? "Sending…" : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── App Component ────────────────────────────────────────────────────────────
function App() {
  var location = useLocation();
  var isHomePage = location.pathname === "/";
  var headerRef = useRef(null);
  var [menuOpen, setMenuOpen] = useState(false);
  var [productsOpen, setProductsOpen] = useState(false);

  // Close menu on route change
  useEffect(function () {
    setMenuOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(function () {
    if (!menuOpen) return;
    function onOutside(e) {
      if (!e.target.closest(".navbar-container") && !e.target.closest(".hamburger-btn")) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("click", onOutside);
    return function () { document.removeEventListener("click", onOutside); };
  }, [menuOpen]);

  // Header scroll effect
  useEffect(function () {
    function handleScroll() {
      if (!headerRef.current) return;
      if (window.scrollY > 20) {
        headerRef.current.classList.add("scrolled");
      } else {
        headerRef.current.classList.remove("scrolled");
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return function () {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  var clientLogos = [
    { clientName: "Property Maintenance", clientLogo: "prop-maintenance.png" },
    { clientName: "Digital Labs",         clientLogo: "digital-labs.png"     },
    { clientName: "Crest",                clientLogo: "crest.png"            },
    { clientName: "Cuvoo",                clientLogo: "Cuvoo.png"            },
    { clientName: "Ansal",                clientLogo: "ansal.png"            },
    { clientName: "Flizone",              clientLogo: "Flizone.png"          },
    { clientName: "Indian Innovation",    clientLogo: "IndianInnovation.png" },
    { clientName: "NewBee Works",         clientLogo: "NewbeeLogo.png"       },
    { clientName: "Oculus",               clientLogo: "oculus_logo.png"      },
    { clientName: "BookGuru",             clientLogo: "bookguru.png"         },
    { clientName: "KlapKlap",             clientLogo: "Klapklap.png"         },
    { clientName: "Compu Trader",         clientLogo: "computrader.png"      },
  ];

  var initialReviews = [
    {
      short: "Our website was developed from NA Tech Ventures and they did an amazing job. I Would recommend them for any development related project.",
      long:  "Our website was developed from NA Tech Ventures and they did an amazing job. I Would recommend them for any development related project.",
      name:  "AMIT PANDEY",
      designation:  "Director",
      organization: "Ansal Infosys",
      image:  "AmitPandey.jpeg",
      active: false,
    },
    {
      short: "We got our mobile application developed from NA Tech Ventures and they did amazing work. They delivered the mobile app on time with all the requirements.",
      long:  "We got our mobile application developed from NA Tech Ventures and they did amazing work. They delivered the mobile app on time with all the requirements. A great set of developers who understands each and every requirement and deliver it accordingly. I would love to work with them in future for our products.",
      name:  "RAMESH SOLANKI",
      designation:  "Director",
      organization: "Flizone Stores Pvt Ltd",
      image:  "Ramesh_Flizone.jpg",
      active: true,
    },
    {
      short: "NA Tech Ventures was entrusted with the task of designing the logo for our Company. They came out with a highly impressive & creative design which was a total delight to us.",
      long:  "NA Tech Ventures was entrusted with the task of designing the logo for our Company. They came out with a highly impressive & creative design which was a total delight to us. We wish them grand success in their field of business.",
      name:  "SUMANT KUMAR SINHA",
      designation:  "CEO",
      organization: "Crest Engineering & Plastic Private Ltd",
      image:  "SumantKrSinha_Crest.jpeg",
      active: false,
    },
  ];

  var reviewState = useState(initialReviews);
  var customerReview = reviewState[0];
  var updateCustomerReview = reviewState[1];

  function showCustomerReview(index) {
    var updated = customerReview.map(function (review, i) {
      return Object.assign({}, review, { active: i === index });
    });
    updateCustomerReview(updated);
  }

  return (
    <div className={"main-container"}>
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <header className={"main-header"} ref={headerRef}>
        <div className={"logo-container"}>
          <img src={require("./images/logo.png").default} alt={"NA Tech Ventures"} />
        </div>

        {/* Desktop nav */}
        <nav className={"navbar-container"}>
          {isHomePage ? (
            <>
              <a href={"/"} className={"menu-item"}>Home</a>
              <a href={"#about"} className={"menu-item"}>About</a>
              <a href={"#services"} className={"menu-item"}>Services</a>
              <a href={"#work"} className={"menu-item"}>Work</a>
              <a href={"#team"} className={"menu-item"}>Team</a>
              <a href={"#customer-review"} className={"menu-item"}>Reviews</a>
              <a href={"#clients"} className={"menu-item"}>Clients</a>
              <a href={"#contact"} className={"menu-item"}>Contact</a>
            </>
          ) : (
            <>
              <Link to={"/"} className={"menu-item"}>Home</Link>
              <Link to={"/"} className={"menu-item"}>About</Link>
              <Link to={"/"} className={"menu-item"}>Services</Link>
              <Link to={"/"} className={"menu-item"}>Work</Link>
              <Link to={"/"} className={"menu-item"}>Team</Link>
              <Link to={"/"} className={"menu-item"}>Reviews</Link>
              <Link to={"/"} className={"menu-item"}>Clients</Link>
              <Link to={"/"} className={"menu-item"}>Contact</Link>
            </>
          )}
          <div className={"menu-item dropdown"}>
            <span className={"dropdown-trigger"}>Products &#9662;</span>
            <div className={"dropdown-menu"}>
              <Link to={"/products/swiperush"} className={"dropdown-item"}>
                SwipeRush
              </Link>
            </div>
          </div>
          <Link to={"/services/basic-website"} className={"menu-item menu-item--cta"}>
            Book Website
          </Link>
        </nav>

        {/* Mobile hamburger button */}
        <button
          className={"hamburger-btn" + (menuOpen ? " open" : "")}
          onClick={function () { setMenuOpen(function (v) { return !v; }); }}
          aria-label={"Toggle menu"}
        >
          <span></span><span></span><span></span>
        </button>

        {/* Mobile slide-down menu */}
        {menuOpen && (
          <nav className={"mobile-nav"}>
            {isHomePage ? (
              <>
                <a href={"/"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Home</a>
                <a href={"#about"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>About</a>
                <a href={"#services"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Services</a>
                <a href={"#work"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Work</a>
                <a href={"#team"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Team</a>
                <a href={"#customer-review"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Reviews</a>
                <a href={"#clients"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Clients</a>
                <a href={"#contact"} className={"mobile-nav-item"} onClick={function () { setMenuOpen(false); }}>Contact</a>
              </>
            ) : (
              <>
                <Link to={"/"} className={"mobile-nav-item"}>Home</Link>
                <Link to={"/"} className={"mobile-nav-item"}>About</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Services</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Work</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Team</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Reviews</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Clients</Link>
                <Link to={"/"} className={"mobile-nav-item"}>Contact</Link>
              </>
            )}
            {/* Products accordion */}
            <div className={"mobile-nav-item mobile-nav-accordion" + (productsOpen ? " open" : "")}
                 onClick={function () { setProductsOpen(function (v) { return !v; }); }}>
              <span>Products <span className={"accordion-arrow"}>&#9662;</span></span>
            </div>
            {productsOpen && (
              <Link to={"/products/swiperush"} className={"mobile-nav-sub-item"}
                    onClick={function () { setMenuOpen(false); setProductsOpen(false); }}>
                SwipeRush
              </Link>
            )}
            <Link to={"/services/basic-website"} className={"mobile-nav-item mobile-nav-item--cta"}
                  onClick={function () { setMenuOpen(false); }}>
              Book Website
            </Link>
          </nav>
        )}
      </header>

      {/* ─── Routes ─────────────────────────────────────────────────────── */}
      <div className={"body-main-container"}>
        <Switch>
          <Route exact path={"/"}>
            <HomePage
              clientLogos={clientLogos}
              customerReview={customerReview}
              showCustomerReview={showCustomerReview}
            />
          </Route>
          <Route exact path={"/products/swiperush"}>
            <SwipeRush />
          </Route>
          <Route exact path={"/products/swiperush/privacy-policy"}>
            <SwipeRushPrivacy />
          </Route>
          <Route exact path={"/products/swiperush/terms-of-service"}>
            <SwipeRushTerms />
          </Route>
          <Route exact path={"/services/basic-website"}>
            <BasicWebsite />
          </Route>
          <Route exact path={"/payment-success"}>
            <PaymentSuccess />
          </Route>
        </Switch>
      </div>

      {/* ─── Footer ─────────────────────────────────────────────────────── */}
      <footer>
        <div className={"footer-grid"}>
          <div className={"footer-brand"}>
            <img
              className={"footer-logo"}
              src={require("./images/logo.png").default}
              alt={"NA Tech Ventures"}
            />
            <p className={"footer-tagline"}>
              Nuevo Alture Tech Ventures — building smarter web and mobile
              products since 2017. Turning ideas into reality, one project at a time.
            </p>
          </div>

          <div className={"footer-col"}>
            <p className={"footer-col-title"}>Navigate</p>
            {isHomePage ? (
              <>
                <a href={"#about"}           className={"footer-link"}>About Us</a>
                <a href={"#services"}        className={"footer-link"}>Services</a>
                <a href={"#customer-review"} className={"footer-link"}>Reviews</a>
                <a href={"#clients"}         className={"footer-link"}>Clients</a>
              </>
            ) : (
              <>
                <Link to={"/"} className={"footer-link"}>Home</Link>
                <Link to={"/"} className={"footer-link"}>About Us</Link>
                <Link to={"/"} className={"footer-link"}>Services</Link>
              </>
            )}
            <Link to={"/products/swiperush"} className={"footer-link"}>SwipeRush</Link>
            <Link to={"/services/basic-website"} className={"footer-link"}>Book a Website</Link>
          </div>

          <div className={"footer-col"}>
            <p className={"footer-col-title"}>Contact</p>
            <a
              href={"mailto:contact@nuevoalture.com"}
              className={"footer-link"}
            >
              contact@nuevoalture.com
            </a>
            <span className={"footer-link"}>Bangalore, India</span>
          </div>
        </div>

        <div className={"footer-bottom"}>
          <p className={"copy-right"}>
            &copy;2026 Nuevo Alture Tech Ventures LLP. All rights reserved.
          </p>

          <div className={"social-media"}>
            <a
              href={"https://www.facebook.com/natech17/"}
              target={"_blank"}
              rel={"noreferrer"}
              aria-label={"Facebook"}
            >
              <i className={"icon icon-facebook"}></i>
            </a>
            <a
              href={"https://www.linkedin.com/company/nuevo-alture-tech-ventures-llp"}
              target={"_blank"}
              rel={"noreferrer"}
              aria-label={"LinkedIn"}
            >
              <i className={"icon icon-linkedin"}></i>
            </a>
            <a
              href={"https://wa.me/+910000000000?text=Hi"}
              target={"_blank"}
              rel={"noreferrer"}
              aria-label={"WhatsApp"}
            >
              <i className={"icon icon-whatsapp"}></i>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
